/* ==========================================================================
   DSM · pickup map (26 Oct 2026)

   A live map of an excursion's pickup areas, in two places on the tour page:

     card  the Pickup area box in the Overview tab (small map)
     tab   the Pickup info tab, beside the list of areas and times (big map)

   Data comes from the page itself: the product's tour.pickup_zones (names and
   times, exactly as filed) and snippets/dsm-pickup-places.liquid (where each
   named area is). A zone whose name matches no place gets no pin but stays in
   the list. No pin at all: no map, and the page looks exactly as before.

   The map is OpenStreetMap through Leaflet, loaded only when a map is about
   to be seen, so pages that never show it pay nothing. If Leaflet cannot be
   loaded the map boxes simply stay hidden.

   Phones: one finger scrolls the page, two fingers move and zoom the map.
   ========================================================================== */
(function () {
  'use strict';

  var LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
  var LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
  var TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

  var PIN = { radius: 7, color: '#ffffff', weight: 2, fillColor: '#1b115c', fillOpacity: 1 };
  var PIN_ON = { radius: 9, color: '#ffffff', weight: 2, fillColor: '#d40e14', fillOpacity: 1 };

  /* --------------------------------------------------------- matching */

  function norm(s) {
    return ' ' + String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() + ' ';
  }

  // "Naiharn Beach, Rawai Beach, Kata Beach" is three areas, "Pakarang /
  // Bangsak" two. Each part takes the first place whose word appears in it.
  function matchPlaces(zoneName, places) {
    var parts = String(zoneName || '').split(/[,\/&]|-|\band\b/i);
    var found = [];
    parts.forEach(function (part) {
      var n = norm(part);
      if (!n.trim()) return;
      for (var i = 0; i < places.length; i++) {
        var pl = places[i];
        var hit = (pl.k || []).some(function (k) { return n.indexOf(' ' + k + ' ') !== -1; });
        if (hit) {
          if (found.indexOf(pl) === -1) found.push(pl);
          break;
        }
      }
    });
    return found;
  }

  // "N/A", "-" and the like are not times.
  function cleanTime(t) {
    var s = String(t || '').trim().replace(/\s*hrs?\.?$/i, '');
    if (!s || /^(n\s*\/?\s*a|-+|–|—)$/i.test(s)) return '';
    // 9.45, 09.45, 9:45 → 09:45 (same rule as the list on the page)
    var m = s.match(/^(\d{1,2})[.:](\d{2})$/);
    if (m && +m[1] < 24) return (m[1].length < 2 ? '0' : '') + m[1] + ':' + m[2];
    return s;
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Exposed for testing and for the theme editor; nothing else uses it.
  window.DSMPickupMap = { match: matchPlaces, cleanTime: cleanTime };

  /* ---------------------------------------------------------- leaflet */

  var loading = null;
  function loadLeaflet() {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (loading) return loading;
    loading = new Promise(function (resolve, reject) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = LEAFLET_CSS;
      document.head.appendChild(css);

      var js = document.createElement('script');
      js.src = LEAFLET_JS;
      js.async = true;
      js.onload = function () { (window.L && window.L.map) ? resolve(window.L) : reject(); };
      js.onerror = reject;
      document.head.appendChild(js);
    });
    return loading;
  }

  /* -------------------------------------------------------------- page */

  function boot(root) {
    var dataEl = root.querySelector('[data-dsm-pmap-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var zones = Array.isArray(data.zones) ? data.zones : [];
    var places = Array.isArray(data.places) ? data.places : [];
    // No pickup list: one pin on the excursion's destination (data.area).
    var area = data.area && isFinite(data.area.a) && isFinite(data.area.o) ? data.area : null;
    if (!zones.length && !area) return;
    if (zones.length && !places.length) return;

    // One pin per PLACE; a place can carry several zone rows.
    var pins = [];
    var pinOf = {};
    if (!zones.length) pins.push({ place: { i: 'area', n: area.n, a: +area.a, o: +area.o }, zones: [] });
    var zonePins = zones.map(function (z, i) {
      return matchPlaces(z && z.zone, places).map(function (pl) {
        if (!pinOf[pl.i]) {
          pinOf[pl.i] = { place: pl, zones: [] };
          pins.push(pinOf[pl.i]);
        }
        pinOf[pl.i].zones.push(i);
        return pinOf[pl.i];
      });
    });
    if (!pins.length) return;

    function tipFor(pin) {
      if (!pin.zones.length) return '<strong>' + esc(pin.place.n || '') + '</strong>';
      return pin.zones.map(function (i) {
        var z = zones[i] || {};
        var t = cleanTime(z.time);
        return '<strong>' + esc(z.zone || '') + '</strong>' + (t ? '<br>' + esc(data.timeWord || 'Pickup') + ' ' + esc(t) : '');
      }).join('<hr>');
    }

    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-dsm-zone-i]'));
    var maps = [];

    function highlight(zoneIdxs, on, openTip) {
      var hit = [];
      zoneIdxs.forEach(function (i) {
        (zonePins[i] || []).forEach(function (p) { if (hit.indexOf(p) === -1) hit.push(p); });
      });
      maps.forEach(function (m) {
        hit.forEach(function (p) {
          var mk = m.markers[p.place.i];
          if (!mk) return;
          mk.setStyle(on ? PIN_ON : PIN);
          if (on) {
            mk.bringToFront();
            if (openTip) mk.openTooltip();
          } else {
            mk.closeTooltip();
          }
        });
      });
      rows.forEach(function (r) {
        var i = Number(r.getAttribute('data-dsm-zone-i'));
        if (zoneIdxs.indexOf(i) !== -1) r.classList.toggle('is-hl', on);
      });
    }

    // The list on the Pickup info tab lights up its pins, and the other way.
    rows.forEach(function (r) {
      var i = Number(r.getAttribute('data-dsm-zone-i'));
      if (!(zonePins[i] || []).length) return;
      r.classList.add('has-pin');
      var on = function () { highlight([i], true, true); };
      var off = function () { highlight([i], false, false); };
      r.addEventListener('mouseenter', on);
      r.addEventListener('mouseleave', off);
      r.addEventListener('focus', on);
      r.addEventListener('blur', off);
    });

    var touch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    function build(box, L) {
      var canvas = box.querySelector('[data-dsm-pmap-canvas]');
      if (!canvas || box._dsmMap) return;
      box.hidden = false;

      var map = L.map(canvas, {
        scrollWheelZoom: false,     // the page scrolls, not the map
        dragging: !touch,           // phones: two fingers move the map
        touchZoom: true,
        tap: false,
        zoomControl: true,
        attributionControl: true
      });
      L.tileLayer(TILES, { maxZoom: 18, attribution: ATTRIBUTION }).addTo(map);
      map.attributionControl.setPrefix(false);

      var markers = {};
      var bounds = [];
      pins.forEach(function (p) {
        var ll = [p.place.a, p.place.o];
        bounds.push(ll);
        var mk = L.circleMarker(ll, PIN).addTo(map);
        mk.bindTooltip(tipFor(p), { direction: 'top', offset: [0, -8], className: 'dsm-pmap__tip' });
        mk.on('mouseover', function () { highlight(p.zones, true, false); });
        mk.on('mouseout', function () { highlight(p.zones, false, false); });
        mk.on('click', function () { mk.openTooltip(); });
        markers[p.place.i] = mk;
      });

      if (!zones.length) map.setView(bounds[0], +area.z || 11);
      else if (bounds.length === 1) map.setView(bounds[0], 13);
      else map.fitBounds(bounds, { padding: [26, 26], maxZoom: 13 });

      // A phone user who drags with one finger is scrolling the page; say how
      // to move the map instead.
      var hint = box.querySelector('[data-dsm-pmap-hint]');
      if (touch && hint) {
        var hide = null;
        canvas.addEventListener('touchmove', function (e) {
          if (e.touches && e.touches.length === 1) {
            hint.hidden = false;
            if (hide) window.clearTimeout(hide);
            hide = window.setTimeout(function () { hint.hidden = true; }, 1600);
          }
        }, { passive: true });
      }

      box._dsmMap = map;
      maps.push({ map: map, markers: markers });

      var wrap = box.closest('[data-dsm-pmap-wrap]');
      if (wrap) wrap.classList.add('has-map');
      window.setTimeout(function () { map.invalidateSize(); }, 0);
    }

    // A map box is built the first time it has a size (the Pickup info tab is
    // hidden until chosen) and has come near the screen.
    var boxes = Array.prototype.slice.call(root.querySelectorAll('[data-dsm-pmap]'));
    boxes.forEach(function (box) {
      var started = false;

      function start() {
        if (started) return;
        started = true;
        loadLeaflet().then(function (L) { build(box, L); }).catch(function () {
          box.hidden = true;               // no map service: nothing is shown
        });
      }

      function visible() {
        // The box itself is hidden until built, so measure its parent.
        var host = box.parentElement;
        return host && host.offsetWidth > 0 && host.offsetHeight > 0;
      }

      var near = !('IntersectionObserver' in window);
      if (!near) {
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) { near = true; if (visible()) start(); }
          });
        }, { rootMargin: '300px' });
        io.observe(box.parentElement);
      }

      // Tab switches change the size: build then, or re-measure a built map.
      if ('ResizeObserver' in window) {
        new ResizeObserver(function () {
          if (box._dsmMap) {
            box._dsmMap.invalidateSize();
          } else if (near && visible()) {
            start();
          }
        }).observe(box.parentElement);
      } else if (near && visible()) {
        start();
      }
    });
  }

  function init() {
    document.querySelectorAll('[data-dsm-pmap-root]').forEach(function (root) {
      if (root._dsmPmap) return;
      root._dsmPmap = true;
      boot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
